import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { LayoutGrid } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { STARTFENSTER_GROUPS, ALL_STARTFENSTER_KEYS } from '@/pages/Dashboard/startfensterConfig';

/**
 * Dialog zum Ein-/Ausblenden einzelner Startfenster-Bereiche und -Kacheln.
 */
export function StartfensterCustomizeDialog({ open, onOpenChange, hidden, onSave }) {
  const [draftHidden, setDraftHidden] = useState(hidden || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraftHidden(hidden || []);
  }, [open, hidden]);

  const hiddenSet = useMemo(() => new Set(draftHidden), [draftHidden]);

  const isOn = (key) => !hiddenSet.has(key);

  const setOn = (key, on) => {
    setDraftHidden((prev) => {
      const s = new Set(prev);
      if (on) s.delete(key);
      else s.add(key);
      return [...s];
    });
  };

  const setGroupOn = (group, on) => {
    const keys = group.items.map((i) => i.key);
    setDraftHidden((prev) => {
      const s = new Set(prev);
      keys.forEach((k) => { if (on) s.delete(k); else s.add(k); });
      return [...s];
    });
  };

  const groupState = (group) => {
    const ons = group.items.filter((i) => !hiddenSet.has(i.key)).length;
    if (ons === 0) return false;
    if (ons === group.items.length) return true;
    return 'indeterminate';
  };

  const showAll = () => setDraftHidden([]);
  const hideAll = () => setDraftHidden([...ALL_STARTFENSTER_KEYS]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(draftHidden);
      toast.success('Startfenster gespeichert');
      onOpenChange(false);
    } catch {
      toast.error('Speichern fehlgeschlagen – lokal gespeichert');
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] flex flex-col" data-testid="startfenster-customize-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-brand-red" />
            Startfenster anpassen
          </DialogTitle>
          <DialogDescription>
            Einzelne Bereiche und Kacheln ein- oder ausblenden. Die Auswahl gilt für Ihr Benutzerkonto.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 flex-wrap mb-2">
          <Button type="button" variant="outline" size="sm" onClick={showAll}>
            Alles einblenden
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={hideAll}>
            Alles ausblenden
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 pr-1 space-y-5 min-h-0">
          {STARTFENSTER_GROUPS.map((group) => {
            const state = groupState(group);
            return (
              <div key={group.id} className="border border-slate-200 rounded-lg p-3">
                <label className="flex items-center gap-2 mb-2 cursor-pointer">
                  <Checkbox
                    checked={state === 'indeterminate' ? 'indeterminate' : state}
                    onCheckedChange={(v) => setGroupOn(group, !!v)}
                    aria-label={group.title}
                  />
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                    {group.title}
                  </span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pl-6">
                  {group.items.map((item) => (
                    <label
                      key={item.key}
                      className="flex items-center gap-2 py-1 cursor-pointer text-sm text-slate-700 hover:text-slate-900"
                    >
                      <Checkbox
                        checked={isOn(item.key)}
                        onCheckedChange={(v) => setOn(item.key, !!v)}
                        data-testid={`startfenster-toggle-${item.key}`}
                      />
                      <span>{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="mt-3 gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-brand-red hover:bg-brand-red/90"
            data-testid="startfenster-save-btn"
          >
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
